import { AnimatePresence } from 'framer-motion';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDraftEntryStore } from '@/pages/Schedule/stores';
import { useToast, MotionBox } from '@/ui';
import { useEntryModalContext } from '../contexts';

const useMountWhen = (mountCondition: boolean) => {
  const [isMounted, setIsMounted] = useState(false);

  useEffect(() => {
    if (mountCondition) {
      setIsMounted(true);
    }
  }, [mountCondition, setIsMounted]);

  const unmount = () => setIsMounted(false);

  return { isMounted, unmount };
};

export const DuplicateStatus = () => {
  const { t } = useTranslation('entryModal');
  const { cloneStatus } = useEntryModalContext();
  const toast = useToast();
  const draftEntry = useDraftEntryStore((store) => store.draftEntry);

  const isDefault = cloneStatus === 'default';
  const isCloning = cloneStatus === 'cloning';
  const isDone = cloneStatus === 'done';
  const key = isCloning ? 'cloning' : isDone ? 'done' : 'default';

  useEffect(() => {
    if (isDone && draftEntry) {
      toast.notify(t('duplicate.toast', { title: draftEntry.title }));
    }
  }, [isDone, toast, draftEntry, t]);

  const { isMounted, unmount } = useMountWhen(isCloning);
  if (!isMounted) {
    return null;
  }

  return (
    <AnimatePresence mode="popLayout">
      <MotionBox
        animate={{ opacity: 1 }}
        color={isDone ? 'green.500' : undefined}
        exit={{ opacity: 0 }}
        fontSize="sm"
        fontWeight="normal"
        initial={{ opacity: 0 }}
        key={key}
        whiteSpace="nowrap"
        onAnimationComplete={() => {
          if (isDefault) {
            // unmount after returning to the default state from the done state.
            // unmounting in this callback allows the fade out animation to complete,
            // and unmounting n general prevents an empty div from taking up flex
            // space on mobile
            unmount();
          }
        }}
      >
        {isCloning
          ? t('duplicate.loading')
          : isDone
            ? t('duplicate.done')
            : null}
      </MotionBox>
    </AnimatePresence>
  );
};
